import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft, Truck } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { getErrorMessage } from '../utils/apiError';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = {
  plate_no: '',
  description: '',
  last_service_date: '',
  status: 'active',
  notes: '',
};

const daysFromToday = (iso) => {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(iso);
  target.setHours(0, 0, 0, 0);
  return Math.round((target - today) / (1000 * 60 * 60 * 24));
};

const StatusBadge = ({ iso }) => {
  const d = daysFromToday(iso);
  if (d === null) return <Badge variant="outline">No date</Badge>;
  if (d < 0) return <Badge className="bg-red-600 text-white">Overdue {Math.abs(d)}d</Badge>;
  if (d === 0) return <Badge className="bg-red-500 text-white">Due TODAY</Badge>;
  if (d <= 7) return <Badge className="bg-orange-500 text-white">Due in {d}d</Badge>;
  if (d <= 30) return <Badge className="bg-yellow-500 text-white">Due in {d}d</Badge>;
  return <Badge className="bg-green-600 text-white">OK ({d}d)</Badge>;
};

export const LorryManagement = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY });

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/lorries`);
      setItems(res.data || []);
    } catch (e) {
      toast.error(getErrorMessage(e, 'Failed to load lorries'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY });
    setShowModal(true);
  };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      plate_no: it.plate_no || '',
      description: it.description || '',
      last_service_date: it.last_service_date || '',
      status: it.status || 'active',
      notes: it.notes || '',
    });
    setShowModal(true);
  };

  const save = async () => {
    if (!form.plate_no) {
      toast.error('Plate number is required');
      return;
    }
    try {
      if (editingId) {
        await axios.put(`${BACKEND_URL}/api/lorries/${editingId}`, form);
        toast.success('Lorry updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/lorries`, form);
        toast.success('Lorry added');
      }
      setShowModal(false);
      fetch();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Save failed'));
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this lorry record?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/lorries/${id}`);
      toast.success('Deleted');
      fetch();
    } catch (e) {
      toast.error(getErrorMessage(e, 'Delete failed'));
    }
  };

  const sendWhatsAppReminder = (it) => {
    const adminPhone = '6598366186';
    const msg = `🔔 NJK Lorry Service Reminder\nPlate: ${it.plate_no}\n${it.description}\nNext Service: ${it.next_service_date} (${daysFromToday(it.next_service_date)}d)`;
    const url = `https://wa.me/${adminPhone}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank');
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-40 pb-12">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button variant="outline" onClick={() => navigate('/admin/billing')} data-testid="back-btn">
              <ArrowLeft size={20} />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><Truck /> Lorry Servicing</h1>
              <p className="text-gray-600">Auto-reminder every 3 months from last service date</p>
            </div>
          </div>
          <Button onClick={openCreate} data-testid="add-lorry-btn">
            <Plus size={18} className="mr-2" /> Add Lorry
          </Button>
        </div>

        <Card>
          <CardHeader><CardTitle>Lorry List</CardTitle></CardHeader>
          <CardContent>
            {loading ? <p className="text-gray-500">Loading...</p>
              : items.length === 0 ? (
                <div className="text-center py-12 text-gray-500">No lorries yet. Click &quot;Add Lorry&quot; to start.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3">Plate No.</th>
                        <th className="text-left p-3">Description</th>
                        <th className="text-left p-3">Last Service</th>
                        <th className="text-left p-3">Next Service</th>
                        <th className="text-left p-3">Status</th>
                        <th className="text-right p-3">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((it) => (
                        <tr key={it.id} className="border-b hover:bg-gray-50" data-testid={`lorry-row-${it.id}`}>
                          <td className="p-3 font-bold">{it.plate_no}</td>
                          <td className="p-3">{it.description}</td>
                          <td className="p-3">{it.last_service_date || '-'}</td>
                          <td className="p-3">{it.next_service_date || '-'}</td>
                          <td className="p-3"><StatusBadge iso={it.next_service_date} /></td>
                          <td className="p-3 text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" variant="outline" onClick={() => sendWhatsAppReminder(it)} title="Send WhatsApp">📱</Button>
                              <Button size="sm" variant="outline" onClick={() => openEdit(it)}><Edit size={14} /></Button>
                              <Button size="sm" variant="outline" onClick={() => remove(it.id)}><Trash2 size={14} className="text-red-600" /></Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </CardContent>
        </Card>

        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingId ? 'Edit Lorry' : 'Add Lorry'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Plate No. *</Label>
                <Input value={form.plate_no} onChange={(e) => setForm({ ...form, plate_no: e.target.value.toUpperCase() })}
                       placeholder="e.g. SHA1234A" data-testid="form-plate" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                       placeholder="e.g. 10-ton lorry crane" data-testid="form-description" />
              </div>
              <div>
                <Label>Last Service Date</Label>
                <Input type="date" value={form.last_service_date}
                       onChange={(e) => setForm({ ...form, last_service_date: e.target.value })}
                       data-testid="form-last-service" />
                <p className="text-xs text-gray-500 mt-1">Next service will be auto-calculated to +3 months.</p>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="retired">Retired</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
              <Button onClick={save} data-testid="form-save">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default LorryManagement;
